import { untrack } from "svelte";
import { AppError, unexpectedError } from "../api/problem";

/**
 * A small stale-while-revalidate cache for read endpoints.
 *
 * Entries are keyed by a JSON-able tuple (`["admin", "customer", id, "quotes"]`), so a
 * write can invalidate exactly the resources it touched — or a whole prefix — instead
 * of every page refetching every list. Concurrent reads of one key share one request,
 * a fresh entry is served without a request at all, and a stale one is served at once
 * while a background refetch replaces it. Nothing here knows about any endpoint: the
 * caller supplies the fetcher with the key.
 *
 * Reactivity: each entry's fields are runes, and a revision counter covers entries
 * that do not exist yet, so `client.get(key)` or a `createQuery` getter read inside a
 * template or `$derived` updates when the entry is created, filled, or refetched.
 * Entries are only ever created from effects, event handlers, and plain calls — never
 * while a `$derived` is evaluating — so reading the cache never mutates it.
 */

export type QueryKeyPart = string | number | boolean | null | undefined | { readonly [key: string]: QueryKeyPart };
export type QueryKey = readonly QueryKeyPart[];
export type Fetcher<T> = (signal: AbortSignal) => Promise<T>;

export interface QueryConfig<T> {
  key: QueryKey;
  fetcher: Fetcher<T>;
  /** How long a successful read counts as fresh, in ms. Defaults to the client's. */
  staleTime?: number;
}

export interface QueryOptions<T> extends QueryConfig<T> {
  /** `false` keeps the query idle (no request, `loading` false) until it flips. */
  enabled?: boolean;
}

export interface QuerySnapshot<T> {
  readonly data: T | undefined;
  readonly error: AppError | null;
  /** A request for this key is in flight (first load or background refetch). */
  readonly fetching: boolean;
  /** Epoch ms of the last successful read; 0 when the data was seeded, never read. */
  readonly updatedAt: number;
}

export interface Query<T> {
  readonly data: T | undefined;
  readonly error: AppError | null;
  /** No data yet and none failed: the first read is pending (or about to start). */
  readonly loading: boolean;
  /** Data is on screen and a background refetch is replacing it. */
  readonly refreshing: boolean;
  readonly fetching: boolean;
  /** Force a network read now, even when the entry is fresh. */
  refetch: () => Promise<void>;
  /** Write the cached value directly (a post-mutation patch). */
  set: (value: T | ((previous: T | undefined) => T)) => void;
}

const DEFAULT_STALE_TIME = 30_000;
// Unobserved entries beyond this many are dropped, oldest first. Admin reads are small
// and per-customer, so a count bound is simpler and more predictable than timers.
const DEFAULT_MAX_ENTRIES = 300;

class Entry<T> {
  data = $state.raw<T | undefined>(undefined);
  error = $state.raw<AppError | null>(null);
  fetching = $state(false);
  updatedAt = $state(0);
  /** Marked by `invalidate` or `seed`: refetch on the next read whatever its age. */
  invalidated = true;
  promise: Promise<T> | null = null;
  controller: AbortController | null = null;
  fetcher: Fetcher<T> | null = null;
  staleTime: number | undefined = undefined;
  observers = 0;
  touchedAt = 0;

  constructor(
    readonly key: QueryKey,
    readonly hash: string
  ) {}
}

function stableValue(value: QueryKeyPart): unknown {
  if (value === null || typeof value !== "object") return value ?? null;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => [key, stableValue(value[key])])
  );
}

/** Object parts hash independently of property order; `undefined` reads as absent. */
export function hashKey(key: QueryKey): string {
  return JSON.stringify(key.map(stableValue));
}

function startsWith(key: QueryKey, prefix: QueryKey): boolean {
  if (prefix.length > key.length) return false;
  return prefix.every((part, index) => hashKey([part]) === hashKey([key[index]]));
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export class QueryClient {
  #entries = new Map<string, Entry<unknown>>();
  #revision = $state(0);
  #inFlight = $state(0);
  readonly staleTime: number;
  readonly maxEntries: number;

  constructor(options: { staleTime?: number; maxEntries?: number } = {}) {
    this.staleTime = options.staleTime ?? DEFAULT_STALE_TIME;
    this.maxEntries = options.maxEntries ?? DEFAULT_MAX_ENTRIES;
  }

  /** Requests in flight across every key; reactive. */
  get fetchingCount(): number {
    return this.#inFlight;
  }

  // ----------------------------------------------------------------- reads

  /** Reactive, non-creating lookup. Safe inside templates and `$derived`. */
  snapshot<T>(key: QueryKey): QuerySnapshot<T> | undefined {
    void this.#revision;
    return this.#entries.get(hashKey(key)) as Entry<T> | undefined;
  }

  /** The cached value, if any — fresh or not. Reactive. */
  get<T>(key: QueryKey): T | undefined {
    return this.snapshot<T>(key)?.data;
  }

  isFresh(key: QueryKey, staleTime?: number): boolean {
    const entry = untrack(() => this.#entries.get(hashKey(key)));
    return !!entry && !this.#isStale(entry, staleTime);
  }

  // ---------------------------------------------------------------- fetches

  /** Cached data when fresh; otherwise one shared request for every concurrent caller. */
  fetch<T>(config: QueryConfig<T>): Promise<T> {
    const entry = this.#ensure<T>(config);
    if (entry.data !== undefined && !this.#isStale(entry, config.staleTime)) return Promise.resolve(entry.data);
    return this.#run(entry);
  }

  /** Warm a key ahead of need (hover, focus). Never throws, never refetches fresh data. */
  prefetch<T>(config: QueryConfig<T>): void {
    this.fetch(config).catch(() => {
      /* the error stays on the entry for whoever reads it next */
    });
  }

  /** Always goes to the network, superseding any request already in flight. */
  refetch<T>(config: QueryConfig<T>): Promise<T> {
    const entry = this.#ensure<T>(config);
    return this.#run(entry, true);
  }

  // ---------------------------------------------------------------- writes

  /** Replace (or derive) a cached value, e.g. with the row a write just returned. */
  set<T>(key: QueryKey, value: T | ((previous: T | undefined) => T)): void {
    const entry = this.#ensure<T>({ key });
    const next = typeof value === "function" ? (value as (previous: T | undefined) => T)(entry.data) : value;
    entry.data = next;
    entry.error = null;
    entry.touchedAt = Date.now();
  }

  /**
   * Provide data another read already returned (a list row for its detail page) without
   * claiming it is fresh: it renders immediately and the next observer revalidates it.
   * Never overwrites data the key's own endpoint returned.
   */
  seed<T>(key: QueryKey, value: T): void {
    const existing = this.#entries.get(hashKey(key)) as Entry<T> | undefined;
    if (existing && (existing.updatedAt > 0 || existing.fetching)) return;
    const entry = existing ?? this.#ensure<T>({ key });
    entry.data = value;
    entry.invalidated = true;
  }

  /**
   * Mark every entry under `prefix` stale. Observed entries refetch now (any request
   * already in flight is superseded, so a read that started before the write can't
   * land afterwards); unobserved ones refetch whenever they are next read.
   */
  invalidate(prefix: QueryKey, options: { exact?: boolean } = {}): void {
    for (const entry of this.#matching(prefix, options.exact)) {
      entry.invalidated = true;
      if (entry.observers > 0 && entry.fetcher) {
        this.#run(entry, true).catch(() => {});
      } else if (entry.promise) {
        // Nobody is waiting on it, and its answer predates the write.
        entry.controller?.abort();
      }
    }
  }

  /** Drop entries outright (their next read is a cold load). */
  remove(prefix: QueryKey, options: { exact?: boolean } = {}): void {
    for (const entry of this.#matching(prefix, options.exact)) {
      entry.controller?.abort();
      this.#entries.delete(entry.hash);
    }
    this.#revision += 1;
  }

  /** Forget everything (sign-out, tests). */
  clear(): void {
    for (const entry of this.#entries.values()) entry.controller?.abort();
    this.#entries.clear();
    this.#revision += 1;
  }

  // ------------------------------------------------------------ observation

  /**
   * Register a live reader: fetch if missing or stale, and include the key in
   * invalidation refetches until the returned function is called.
   */
  observe<T>(config: QueryConfig<T>): () => void {
    const entry = this.#ensure<T>(config);
    entry.observers += 1;
    if (entry.data === undefined || this.#isStale(entry, config.staleTime)) {
      this.#run(entry).catch(() => {});
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      entry.observers = Math.max(0, entry.observers - 1);
      entry.touchedAt = Date.now();
      this.#trim();
    };
  }

  // -------------------------------------------------------------- internals

  #isStale(entry: Entry<unknown>, staleTime?: number): boolean {
    if (entry.invalidated || entry.updatedAt === 0) return true;
    const window = staleTime ?? entry.staleTime ?? this.staleTime;
    return Date.now() - entry.updatedAt > window;
  }

  #ensure<T>(config: { key: QueryKey; fetcher?: Fetcher<T>; staleTime?: number }): Entry<T> {
    const hash = hashKey(config.key);
    let entry = this.#entries.get(hash) as Entry<T> | undefined;
    if (!entry) {
      // Make room first, so the entry being created can never be the one evicted.
      this.#trim(1);
      entry = new Entry<T>(config.key, hash);
      this.#entries.set(hash, entry as Entry<unknown>);
      this.#revision += 1;
    }
    if (config.fetcher) entry.fetcher = config.fetcher;
    if (config.staleTime !== undefined) entry.staleTime = config.staleTime;
    entry.touchedAt = Date.now();
    return entry;
  }

  #matching(prefix: QueryKey, exact = false): Entry<unknown>[] {
    const target = hashKey(prefix);
    return [...this.#entries.values()].filter((entry) =>
      exact ? entry.hash === target : startsWith(entry.key, prefix)
    );
  }

  #run<T>(entry: Entry<T>, supersede = false): Promise<T> {
    if (entry.promise && !supersede) return entry.promise;
    const fetcher = entry.fetcher;
    if (!fetcher) return Promise.reject(new Error(`No fetcher registered for ${entry.hash}`));
    entry.controller?.abort();
    const controller = new AbortController();
    entry.controller = controller;
    if (!entry.fetching) this.#inFlight += 1;
    entry.fetching = true;
    // A retry after a failure should read as loading again, not as the old error.
    if (entry.data === undefined) entry.error = null;

    const promise: Promise<T> = fetcher(controller.signal).then(
      (data) => {
        if (entry.controller !== controller) return entry.promise ?? data;
        entry.data = data;
        entry.error = null;
        entry.updatedAt = Date.now();
        entry.invalidated = false;
        return data;
      },
      (error: unknown) => {
        // Superseded by a newer request: hand callers that one's answer instead.
        if (entry.controller !== controller) {
          if (entry.promise && entry.promise !== promise) return entry.promise;
          throw error;
        }
        if (isAbort(error)) throw error;
        entry.error = error instanceof AppError ? error : unexpectedError(error);
        throw entry.error;
      }
    );
    entry.promise = promise;
    promise
      .finally(() => {
        if (entry.controller !== controller) return;
        entry.controller = null;
        entry.promise = null;
        entry.fetching = false;
        this.#inFlight = Math.max(0, this.#inFlight - 1);
      })
      .catch(() => {});
    return promise;
  }

  #trim(reserve = 0): void {
    const limit = this.maxEntries - reserve;
    if (this.#entries.size <= limit) return;
    const idle = [...this.#entries.values()]
      .filter((entry) => entry.observers === 0 && !entry.fetching)
      .sort((left, right) => left.touchedAt - right.touchedAt);
    for (const entry of idle) {
      if (this.#entries.size <= limit) break;
      this.#entries.delete(entry.hash);
    }
  }
}

/** The app-wide cache. Cleared on sign-out and between unit tests. */
export const queryClient = new QueryClient();

/**
 * Bind a component to one cached read. Call during component initialisation: the
 * query observes its key while the component is mounted, follows the key when the
 * options' reactive inputs change, and reads as `loading` only when there is nothing
 * cached to show — a stale value renders at once while `refreshing` is true.
 */
export function createQuery<T>(options: () => QueryOptions<T>, client: QueryClient = queryClient): Query<T> {
  const current = $derived(options());
  const enabled = $derived(current.enabled !== false);
  const entry = $derived(client.snapshot<T>(current.key));

  $effect(() => {
    if (!enabled) return;
    const config: QueryConfig<T> = {
      key: current.key,
      fetcher: current.fetcher,
      ...(current.staleTime !== undefined ? { staleTime: current.staleTime } : {})
    };
    return untrack(() => client.observe(config));
  });

  return {
    get data() {
      return entry?.data;
    },
    get error() {
      return entry?.error ?? null;
    },
    get loading() {
      return enabled && entry?.data === undefined && !entry?.error;
    },
    get refreshing() {
      return !!entry?.fetching && entry.data !== undefined;
    },
    get fetching() {
      return !!entry?.fetching;
    },
    async refetch() {
      await client.refetch(current).catch(() => {});
    },
    set(value) {
      client.set(current.key, value);
    }
  };
}
